import { useCallback, useEffect, useRef, useState } from "react";
import { AchievementsSeen, getSeen, setSeen } from "../utils/achievementsSeenStorage";
import { getCloudProgress, putCloudProgress } from "../utils/progressCloud";
import { useAuth } from "../context/AuthContext";
import { ProgressSyncStatus } from "./progressTypes";

const CLOUD_WRITE_DEBOUNCE_MS = 800;
const FEATURE_KEY = "achievementsSeen";

// Tracks which achievement-unlocked toasts a signed-in user has already been
// shown. Mirrors the use*Progress hook pattern used throughout src/hooks -
// same table, one more feature key, no new infra.
export function useAchievementsSeen() {
    const { status, user } = useAuth();
    const [seen, setSeenState] = useState<AchievementsSeen>(() => getSeen());
    const [syncStatus, setSyncStatus] = useState<ProgressSyncStatus>("local");
    const writeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const latestSeenRef = useRef(seen);
    latestSeenRef.current = seen;

    useEffect(() => {
        if (status === "unauthenticated") {
            setSeenState({});
            setSyncStatus("signedOut");
            return undefined;
        }

        if (status !== "authenticated" || !user?.identityId) {
            setSeenState(getSeen());
            setSyncStatus("local");
            return undefined;
        }

        let cancelled = false;
        setSyncStatus("loading");

        getCloudProgress(user.identityId, FEATURE_KEY)
            .then((remote) => {
                if (cancelled) return;
                if (remote?.data && Object.keys(remote.data).length > 0) {
                    setSeenState(remote.data);
                }
                setSyncStatus("ready");
            })
            .catch(() => {
                if (!cancelled) setSyncStatus("error");
            });

        return () => {
            cancelled = true;
        };
    }, [status, user?.identityId]);

    useEffect(() => {
        return () => {
            if (writeTimeoutRef.current !== null) clearTimeout(writeTimeoutRef.current);
        };
    }, []);

    // A toast is dismissed immediately when it's decided (not on a timer),
    // so navigating away right after it appears still marks it seen.
    const markSeen = useCallback(
        (achievementId: string) => {
            if (status === "unauthenticated") return;
            setSeenState((current) => {
                if (current[achievementId] === "seen") return current;
                const next = { ...current, [achievementId]: "seen" };
                setSeen(next);

                if (status === "authenticated" && user?.identityId) {
                    if (writeTimeoutRef.current !== null) clearTimeout(writeTimeoutRef.current);
                    writeTimeoutRef.current = setTimeout(() => {
                        putCloudProgress(user.identityId, FEATURE_KEY, latestSeenRef.current).catch(() => {
                            setSyncStatus("error");
                        });
                    }, CLOUD_WRITE_DEBOUNCE_MS);
                }

                return next;
            });
        },
        [status, user?.identityId]
    );

    return { seen, markSeen, syncStatus };
}
